import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { FormikActions } from 'formik'
import {
  CreateProvingForm,
  CreateProvingFormSchema,
} from 'forms/CreateProvingForm'
import {
  useCreateProvingCommand,
  useCustomSearchCommand,
  useShowCommand,
} from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import { Proving, UserRole } from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import {
  useCreateProvingMutation,
  useReadProvingsQuery,
} from 'views/ProvingsIndexView/ProvingsIndexView.generated'
import { ProvingInsertType } from '../../model'
import { ProvingDepartmentList } from './ProvingDepartmentList'

const ProvingDepartmentListView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useRouter()

  const [routeParams, updateRouteParams] = useRouteParams<{
    search?: string
  }>('provings', {
    search: undefined,
  })

  const [panelState, showPanel, hidePanel] =
    useFormikPanelState<ProvingInsertType>()

  const showProving = React.useCallback(
    (proving: Partial<Proving>) => {
      router.navigate('provings.details', { id: proving.id })
    },
    [router]
  )
  const showNewProving = React.useCallback(
    (proving: Partial<Proving>) => {
      router.navigate('provings.details', {
        id: proving.id,
        page: 'participants',
      })
    },
    [router]
  )
  const [selection, selectedItems] = useSelection<Partial<Proving>>({
    getKey: (item) => item!.id!,
  })

  useCreateProvingCommand(10, () => showPanel({} as ProvingInsertType))

  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => showProving(selectedItems[0]),
    },
    [selectedItems]
  )

  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.proving.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => {
        updateRouteParams({ search: undefined })
      },
    },
    [routeParams, updateRouteParams]
  )

  const [createProving] = useCreateProvingMutation()

  const { loading, data, refetch, error } = useReadProvingsQuery({
    variables: {
      search: routeParams.search,
    },
  })

  const items =
    data && data.provings ? data.provings.filter((x) => !x._deleted) : []

  const handleSubmit = async (
    values: ProvingInsertType,
    formikActions: FormikActions<ProvingInsertType>
  ) => {
    formikActions.setSubmitting(true)

    const createdProvingJSON = await createProving({
      variables: { input: values },
    })
    const newProvingJson = createdProvingJSON as unknown as {
      data: { createProving: { id: string } }
    }
    const newProving: Partial<Proving> = {
      id: newProvingJson.data.createProving.id,
    }
    await refetch({
      search: routeParams.search,
    })
    formikActions.setSubmitting(false)
    hidePanel()
    showNewProving(newProving)
  }
  return (
    <FlexSpinner loading={loading} error={error}>
      <ProvingDepartmentList
        items={items}
        onItemInvoked={showProving}
        selection={selection}
        disableMarquee={true}
        alwaysCollapsed={true}
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new CreateProvingFormSchema()}
        headerText={t('model.proving.create')}
      >
        <CreateProvingForm />
      </FormikPanel>
    </FlexSpinner>
  )
}

export const ProvingDepartmentView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.SystemAdministrator,
        UserRole.ResourceManager,
        UserRole.ServiceTeam,
        UserRole.ProvingOrganisation,
        UserRole.Reader,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <ProvingDepartmentListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)
